export type NotificationType = "message" | "signature";

export interface NotificationMetadata {
  case_title?: string;
  client_name?: string;
  center?: string;
  client_type?: string | null;
  message_created_at?: string;
  signed_at?: string;
  quote_version?: number;
  company_name?: string | null;
}

export interface NotificationRow {
  id: string;
  user_id: string;
  case_id: string | null;
  type: NotificationType;
  created_at: string;
  read_at: string | null;
  metadata: NotificationMetadata;
}

export interface MessageNotificationGroup {
  caseId: string;
  caseTitle: string;
  clientName: string;
  companyName: string;
  center: string;
  lastMessageAt: string;
  totalCount: number;
  unreadCount: number;
}

export interface SignatureNotificationItem {
  caseId: string;
  caseTitle: string;
  clientName: string;
  companyName: string;
  center: string;
  signedAt: string;
  quoteVersion?: number;
}

interface NotificationGroups {
  messageGroups: MessageNotificationGroup[];
  signatureItems: SignatureNotificationItem[];
  totalUnread: number;
}

const safeString = (value?: string | null, fallback = "") => value ?? fallback;

export function groupNotifications(
  notifications: NotificationRow[],
  selectedCenters: string[]
): NotificationGroups {
  const allowedCenters = selectedCenters.length > 0 ? new Set(selectedCenters) : null;

  const filteredNotifications = notifications.filter((notification) => {
    const center = notification.metadata.center;
    if (allowedCenters && center && !allowedCenters.has(center)) return false;
    return true;
  });

  const unreadNotifications = filteredNotifications.filter(
    (notification) => !notification.read_at
  );

  const messageMap = new Map<string, MessageNotificationGroup>();
  const signatureMap = new Map<string, SignatureNotificationItem>();

  filteredNotifications.forEach((notification) => {
    if (!notification.case_id) return;

    const caseTitle = safeString(notification.metadata.case_title, "Sans titre");
    const clientName = safeString(notification.metadata.client_name, "Client");
    const companyName = safeString(notification.metadata.company_name, "");
    const center = safeString(notification.metadata.center, "");

    if (notification.type === "message") {
      const existing = messageMap.get(notification.case_id);
      const messageDate =
        notification.metadata.message_created_at || notification.created_at;
      const isUnread = !notification.read_at;

      if (!existing) {
        messageMap.set(notification.case_id, {
          caseId: notification.case_id,
          caseTitle,
          clientName,
          companyName,
          center,
          lastMessageAt: messageDate,
          totalCount: 1,
          unreadCount: isUnread ? 1 : 0,
        });
      } else {
        const latest =
          new Date(messageDate).getTime() >
          new Date(existing.lastMessageAt).getTime()
            ? messageDate
            : existing.lastMessageAt;

        messageMap.set(notification.case_id, {
          ...existing,
          lastMessageAt: latest,
          totalCount: existing.totalCount + 1,
          unreadCount: existing.unreadCount + (isUnread ? 1 : 0),
        });
      }
    }

    if (notification.type === "signature" && !notification.read_at) {
      const signedAt = notification.metadata.signed_at || notification.created_at;
      const current = signatureMap.get(notification.case_id);

      if (!current) {
        signatureMap.set(notification.case_id, {
          caseId: notification.case_id,
          caseTitle,
          clientName,
          companyName,
          center,
          signedAt,
          quoteVersion: notification.metadata.quote_version,
        });
      } else if (
        new Date(signedAt).getTime() > new Date(current.signedAt).getTime()
      ) {
        signatureMap.set(notification.case_id, {
          ...current,
          signedAt,
          quoteVersion: notification.metadata.quote_version,
        });
      }
    }
  });

  // Ne garder que les groupes avec au moins 1 message non lu
  const messageGroups = Array.from(messageMap.values())
    .filter((g) => g.unreadCount > 0)
    .sort(
      (a, b) =>
        new Date(b.lastMessageAt).getTime() -
        new Date(a.lastMessageAt).getTime()
    );
  const signatureItems = Array.from(signatureMap.values()).sort(
    (a, b) => new Date(b.signedAt).getTime() - new Date(a.signedAt).getTime()
  );

  return {
    messageGroups,
    signatureItems,
    totalUnread: unreadNotifications.length,
  };
}
